import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Field, Form, Formik } from 'formik';
import styles from '../profile.module.scss';
import { FIELDS, FIELDS_LABELS } from '../types';
import { Admins, CommissionTerms, Contacts, DDStatus, NetworkDetails } from '../partials';
import { Column, Columns } from 'shared/Columns';
import { updateAdmin } from 'utils/api/admins';
import { updateNetwork } from 'utils/api/networks';
import { mapInitialData, mapResources } from './utils';
import validationSchema from './validationSchema';
import { createBody } from '../utils';
import { assignCommission, changeSubmissionRoute, unassignCommission, checkCommissionTermsCoverage } from './api';
import { Props } from './types';
import ChangableSelect from 'shared/Profile/ChangeableSelect';
import { Box } from 'lmc-ui-library';
import { FormikSetFieldError } from 'utils/types';

const DIRECT_SUBMISSION_ROUTE_ID = '0';

const CmNetworkProfile: React.FC<Props> = ({ data, onUpdate }) => {
  const { id: networkId } = useParams<{ id: string }>();
  const fetchedData = data;
  const initialData = mapInitialData(fetchedData);
  const resources = mapResources(fetchedData.resources);
  const shouldCTBeDisabled = fetchedData.company.hasClub;
  const [hasDuplicates, setHasDuplicates] = useState<boolean>(false);
  const [hasMissingCoverage, setHasMissingCoverage] = useState<boolean>(false);

  const handleNetworkChangeClick = (fieldName: string) => (values: any) =>
    updateNetwork(networkId, createBody(fieldName, values));
  const handleAdminChangeClick = (adminId: string) => (fieldName: string) => (values: any) =>
    updateAdmin(adminId, createBody(fieldName, values));

  const validateCommissionTerms = async (networkId: string) => {
    const res = await checkCommissionTermsCoverage(networkId);

    setHasDuplicates(res.duplicates);
    setHasMissingCoverage(res.missingCoverage);
  };

  const assignCommissionTerm = (
    setFieldError: FormikSetFieldError,
    fieldName: string,
    commissionTerms: Array<string>,
  ) => (index: number) => async (commissionTermId: string, previousCommissionId: string) => {
    const filteredCommTermsArray = commissionTerms.filter((ct, ctIndex) => ct !== '' && ctIndex !== index);

    if (filteredCommTermsArray.includes(commissionTermId)) {
      setFieldError(`${fieldName}.${index}`, 'This commission firm has been already applied');
      return false;
    }

    if (previousCommissionId && !filteredCommTermsArray.includes(previousCommissionId)) {
      unassignCommission(networkId, previousCommissionId);
    }
    try {
      await assignCommission(networkId, commissionTermId);
      await validateCommissionTerms(networkId);
      return true;
    } catch (e) {
      setFieldError(
        `${fieldName}.${index}`,
        e.response.data ? e.response.data.details : 'Cannot assign commission term.',
      );
    }
    return false;
  };

  const unassignCommissionTerm = async (commissionTermId: string) => {
    try {
      await unassignCommission(networkId, commissionTermId);
      await validateCommissionTerms(networkId);
    } catch (e) {
      console.log(e);
    }
  };
  const handleChangeSubmissionRouteClick = async (networkId: string, entityId: string) => {
    await changeSubmissionRoute(
      networkId,
      entityId === DIRECT_SUBMISSION_ROUTE_ID ? null : entityId,
      entityId === DIRECT_SUBMISSION_ROUTE_ID ? 'DIRECT' : 'CLUB',
    );
    onUpdate();
  };

  useEffect(() => {
    validateCommissionTerms(networkId);
    //eslint-disable-next-line
  }, []);

  return (
    <>
      <Formik onSubmit={() => {}} initialValues={initialData} validationSchema={validationSchema} enableReinitialize>
        {({ values, setFieldValue, setFieldError }) => (
          <Form>
            <>
              <Columns>
                <Column>
                  <NetworkDetails />
                  <Box title="Preferred submission route" className={styles.box}>
                    <Box.Content>
                      <Field
                        component={ChangableSelect}
                        name={FIELDS.SUBMISSION_ROUTE}
                        title={FIELDS_LABELS.SUBMISSION_ROUTE}
                        options={resources.submissionRoutes}
                        onChangeClick={(submissionRouteId: string) =>
                          handleChangeSubmissionRouteClick(networkId, submissionRouteId)
                        }
                        disabled={false}
                      />
                    </Box.Content>
                  </Box>
                  <CommissionTerms
                    title="Standard Commission terms"
                    singleItemTitle={FIELDS_LABELS.STANDARD_COMMISSION_TERM}
                    fieldName={FIELDS.STANDARD_COMMISSION_TERMS}
                    commissionTerms={resources.standardCommissionTerms}
                    assignCommissionTerm={assignCommissionTerm(
                      setFieldError,
                      FIELDS.STANDARD_COMMISSION_TERMS,
                      values[FIELDS.STANDARD_COMMISSION_TERMS],
                    )}
                    unassignCommissionTerm={unassignCommissionTerm}
                    shouldBeDisabled={shouldCTBeDisabled}
                    showRequired={
                      values.standardCommissionTerms[0] === '' &&
                      !values.standardCommissionTerms[0] &&
                      !values.standardCommissionTerms.some(
                        (standardCommissionTermId: string) => standardCommissionTermId,
                      )
                    }
                    hasDuplicates={hasDuplicates}
                    hasMissingCoverage={hasMissingCoverage}
                  />
                  <CommissionTerms
                    title="Enhanced up-front Commission terms"
                    singleItemTitle={FIELDS_LABELS.ENHANCED_UPFRONT_COMMISSION_TERM}
                    fieldName={FIELDS.ENHANCED_UPFRONT_COMMISSION_TERMS}
                    commissionTerms={resources.enhancedCommissionTerms}
                    assignCommissionTerm={assignCommissionTerm(
                      setFieldError,
                      FIELDS.ENHANCED_UPFRONT_COMMISSION_TERMS,
                      values[FIELDS.ENHANCED_UPFRONT_COMMISSION_TERMS],
                    )}
                    unassignCommissionTerm={unassignCommissionTerm}
                    canDeleteEveryItem={true}
                  />
                  <CommissionTerms
                    title="Ongoing Commission terms"
                    singleItemTitle={FIELDS_LABELS.ONGOING_COMMISSION_TERM}
                    fieldName={FIELDS.ONGOING_COMMISSION_TERMS}
                    commissionTerms={resources.ongoingCommissionTerms}
                    assignCommissionTerm={assignCommissionTerm(
                      setFieldError,
                      FIELDS.ONGOING_COMMISSION_TERMS,
                      values[FIELDS.ONGOING_COMMISSION_TERMS],
                    )}
                    unassignCommissionTerm={unassignCommissionTerm}
                    shouldBeDisabled={shouldCTBeDisabled}
                    canDeleteEveryItem={true}
                  />
                  <Contacts
                    onChangeClick={handleNetworkChangeClick}
                    addressValues={values.address}
                    setFieldValue={setFieldValue}
                  />
                </Column>

                <Column>
                  <Admins
                    title="Network Admins"
                    admins={values[FIELDS.ADMINS]}
                    onChangeClick={handleAdminChangeClick}
                    setFieldValue={setFieldValue}
                  />
                  <DDStatus
                    label={FIELDS_LABELS.DD_STATUS_NETWORK}
                    data={{
                      lmcStatus: values[FIELDS.LMC_STATUS],
                      dueDiligenceStatus: values[FIELDS.DD_STATUS],
                    }}
                    onChangeClick={handleNetworkChangeClick}
                  />
                </Column>
              </Columns>
            </>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default CmNetworkProfile;
